// Small helpers shared by the ui components.
import { useEffect, useId, useRef, useState } from 'react';
import { layout } from '../tokens';

/** Join class names, skipping falsy values. */
export const cx = (...parts) => parts.filter(Boolean).join(' ');

/** True when `query` matches. Safe in jsdom (no matchMedia → false). */
export function useMediaQuery(query) {
  const get = () => {
    try {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false;
    } catch (_) {
      return false;
    }
  };
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

/** The phone layout (≤ 720 px) — see tokens.layout.phoneMax. */
export const PHONE_QUERY = `(max-width: ${layout.phoneMax}px)`;
export const useIsPhone = () => useMediaQuery(PHONE_QUERY);

// Every open overlay (Layer, Popover) registers here in open order; Escape
// only reaches the TOPMOST one, so one key press closes one layer.
const escStack = [];

/**
 * Calls `handler` on Escape while `active`, only when this is the most
 * recently opened active overlay. Listens in the capture phase on document,
 * because overlays stop key/click propagation at their panel (so events do not
 * reach a clickable row underneath through React's portal bubbling).
 */
export function useEscape(active, handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!active) return undefined;
    const token = {};
    escStack.push(token);
    const onKey = (e) => {
      if (e.key === 'Escape' && escStack[escStack.length - 1] === token && ref.current) ref.current(e);
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = escStack.indexOf(token);
      if (i >= 0) escStack.splice(i, 1);
    };
  }, [active]);
}

/**
 * Handlers that stop an event at an overlay panel. React bubbles events
 * through portals along the COMPONENT tree, so without this a click on a
 * menu item inside a clickable table row would also fire the row's onClick.
 */
const stop = (e) => e.stopPropagation();
export const STOP_PROPAGATION = { onClick: stop, onMouseDown: stop, onTouchStart: stop, onKeyDown: stop, onDoubleClick: stop, onContextMenu: stop };

/** Locks page scroll while `active` (dialogs, drawers, sheets). */
export function useScrollLock(active) {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

/** Stable id for aria wiring. */
export const useUid = () => useId();
